import UserAvatar from './UserAvatar';
import { Icon } from '@iconify/react';
import { User, ChevronsUpDown, LogOut, CircleDollarSign, Briefcase, Settings, Moon, Sun, Monitor, Palette } from 'lucide-react';
import { PLANBASE_CONFIG } from '../config';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();

interface NavigationItem {
  name: string;
  href: string;
  icon: string;
}

const navigation: NavigationItem[] = [
  { name: 'Discover', href: '/discover', icon: 'hugeicons:compass' },
  { name: 'Portfolio', href: '/portfolio', icon: 'hugeicons:folder-01' },
  { name: 'Orbi AI', href: '/orbit-ai', icon: 'hugeicons:ai-magic' },
  { name: 'Collection', href: '/collections', icon: 'hugeicons:bookmark-01' },
  // Admin removed from bottom nav — accessible via Profile page
];

type ThemeMode = 'light' | 'dark' | 'system';

function applyTheme(mode: ThemeMode) {
  if (mode === 'dark') document.documentElement.classList.add('dark');
  else if (mode === 'light') document.documentElement.classList.remove('dark');
  else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>('light');
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const saved = localStorage.getItem('planbase_theme') as ThemeMode;
    if (saved) setTheme(saved);
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    }
    if (popoverOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [popoverOpen]);

  const handleTheme = (mode: ThemeMode) => {
    setTheme(mode);
    localStorage.setItem('planbase_theme', mode);
    applyTheme(mode);
  };

  const handleLogout = () => {
    setPopoverOpen(false);
    logout();
    navigate('/login');
  };

  const go = (path: string) => { setPopoverOpen(false); navigate(path); };

  const displayName = user?.name || user?.email?.split('@')[0] || 'Account';

  return (
    <div className="fixed bottom-0 left-0 right-0 md:sticky md:top-0 md:h-screen md:inset-y-0 md:left-auto md:right-auto w-full md:w-64 md:bg-background md:border-t-0 flex md:flex-col z-50 md:flex-shrink-0 bg-background/60 backdrop-blur-xl backdrop-saturate-150 border-t border-white/10 dark:border-white/5" style={isNative ? { paddingBottom: 'env(safe-area-inset-bottom)' } : {}}>

      {/* Logo — desktop only */}
      <div className="hidden md:flex p-4 px-4 items-center justify-start">
        {PLANBASE_CONFIG.project.logo_url ? (
          <div className="flex items-center gap-2">
            <img src={PLANBASE_CONFIG.project.logo_url} alt="Planbase" className="h-7 w-7 rounded-full object-cover flex-shrink-0" />
            <span className="text-sm font-medium text-foreground">Planbase</span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
              <Icon icon="hugeicons:compass" className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-medium text-foreground">Planbase</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex md:flex-col flex-1 px-2 py-2 md:py-4 md:space-y-0.5 justify-around md:justify-start">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`flex flex-col md:flex-row items-center justify-center md:justify-start py-2 px-2 md:px-3 rounded-lg transition-all gap-0 md:gap-2.5 ${
                isActive
                  ? 'text-orange-500 border border-transparent md:bg-muted md:border-border/40'
                  : 'text-muted-foreground hover:text-foreground border border-transparent hover:bg-white hover:border-border/40 dark:hover:bg-muted dark:hover:border-transparent'
              }`}
            >
              <Icon icon={item.icon} className="h-5 w-5 mb-0.5 md:mb-0 flex-shrink-0" />
              <span className={`text-[10px] md:text-xs ${isActive ? 'font-medium' : 'font-normal'}`}>
                {item.name}
              </span>
            </Link>
          );
        })}

        {/* Mobile: profile link */}
        <Link
          to="/profile"
          className={`md:hidden flex flex-col items-center justify-center py-2 px-2 rounded-lg transition-all border border-transparent ${
            location.pathname === '/profile' || location.pathname.startsWith('/profile/')
              ? 'text-orange-500'
              : 'text-muted-foreground hover:text-foreground hover:bg-white hover:border-border/40'
          }`}
        >
          <User className="h-5 w-5 mb-0.5" />
          <span className="text-[10px]">Profile</span>
        </Link>
      </nav>

      {/* Footer — desktop only */}
      <div className="hidden md:block px-2 pb-2 md:px-3 md:pb-3 relative" ref={popoverRef}>

        {/* Popover */}
        {popoverOpen && (
          <div className="absolute bottom-full left-0 right-0 mb-2 bg-background border border-border rounded-2xl shadow-xl z-50 overflow-hidden">
            <div className="px-4 py-4 bg-[#f0f0f0] dark:bg-muted/50 rounded-b-2xl border-b border-border">
              <p className="text-base font-semibold text-foreground leading-tight truncate">{displayName}</p>
              <p className="text-xs text-muted-foreground mt-1 truncate">{user?.email}</p>
            </div>

            <div className="space-y-0.5 p-1">
              <button onClick={() => go('/profile/edit')} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-foreground border border-transparent hover:bg-white hover:border-border/40 dark:hover:bg-muted dark:hover:border-transparent transition-colors text-left">
                <Settings className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                Profile Settings
              </button>
              <button onClick={() => go('/portfolio-setup')} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-foreground border border-transparent hover:bg-white hover:border-border/40 dark:hover:bg-muted dark:hover:border-transparent transition-colors text-left">
                <Briefcase className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                Portfolio Setup
              </button>
              <button onClick={() => go('/admin')} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-foreground border border-transparent hover:bg-white hover:border-border/40 dark:hover:bg-muted dark:hover:border-transparent transition-colors text-left">
                <Icon icon="hugeicons:user-shield" className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                Admin
              </button>
              <button onClick={() => go('/pricing')} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-foreground border border-transparent hover:bg-white hover:border-border/40 dark:hover:bg-muted dark:hover:border-transparent transition-colors text-left">
                <CircleDollarSign className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                Billing
              </button>

              <div className="border-t border-border" />

              <button onClick={handleLogout} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-foreground border border-transparent hover:bg-white hover:border-border/40 dark:hover:bg-muted dark:hover:border-transparent transition-colors text-left">
                <LogOut className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                Log out
              </button>

              <div className="flex items-center gap-2.5 px-3 py-2">
                <Palette className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                <span className="text-xs text-foreground flex-1">Theme</span>
                <div className="flex items-center gap-0.5 bg-muted rounded-lg p-0.5">
                  <button onClick={() => handleTheme('dark')} className={`p-1.5 rounded-md transition-colors ${theme === 'dark' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                    <Moon className="h-3 w-3" />
                  </button>
                  <button onClick={() => handleTheme('light')} className={`p-1.5 rounded-md transition-colors ${theme === 'light' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                    <Sun className="h-3 w-3" />
                  </button>
                  <button onClick={() => handleTheme('system')} className={`p-1.5 rounded-md transition-colors ${theme === 'system' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                    <Monitor className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={() => setPopoverOpen(v => !v)}
          className="flex w-full items-center gap-2 pl-1 pr-2 py-1 rounded-xl border border-border hover:bg-white dark:hover:bg-muted transition-colors"
        >
          <UserAvatar name={user?.name} avatarUrl={user?.avatar_url} size="sm" className="!h-5 !w-5 !text-[9px] flex-shrink-0" />
          <span className="text-xs font-normal text-foreground flex-1 text-left truncate">{displayName}</span>
          <ChevronsUpDown className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        </button>
      </div>
    </div>
  );
}
